/**
 * API Test Harness — Endpoint Configuration
 *
 * Each group contains endpoints with prepopulated test parameters.
 * To add a new endpoint, add an entry to the appropriate group's `endpoints` array.
 *
 * Parameter locations: 'body' | 'query' | 'route' | 'header'
 * Parameter types:     'text' | 'number' | 'boolean' | 'select' | 'json' | 'textarea'
 * Danger levels:       null | 'warning' | 'critical'
 */

// ---------------------------------------------------------------------------
// Environments
// ---------------------------------------------------------------------------
export const ENVIRONMENTS = [
  { id: 'local', label: 'Local', url: 'http://localhost:7071/api', dangerous: false },
  { id: 'staging', label: 'Staging', url: 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api', dangerous: false },
  { id: 'production', label: 'Production', url: 'https://func.qolsync.ai/api', dangerous: true },
];

// ---------------------------------------------------------------------------
// Prepopulated test data
// ---------------------------------------------------------------------------
export const TEST_DATA = {
  stripeCards: [
    { label: 'Visa (success)', value: '4242424242424242' },
    { label: 'Visa (decline)', value: '4000000000000002' },
    { label: 'Insufficient funds', value: '4000000000009995' },
    { label: '3DS required', value: '4000000000003220' },
  ],
  eventPassTiers: [
    { label: 'Starter ($99)', value: 'event_pass_starter' },
    { label: 'Professional ($199)', value: 'event_pass_professional' },
    { label: 'Business ($499)', value: 'event_pass_business' },
    { label: 'Enterprise100 ($799)', value: 'event_pass_enterprise100' },
    { label: 'Enterprise300 ($999)', value: 'event_pass_enterprise300' },
    { label: 'Enterprise500 ($1,199)', value: 'event_pass_enterprise500' },
  ],
  // The packs `PackProductCatalog.TryGetSellablePack` accepts — the usage-credit generation,
  // quoted in POINTS at the catalog's own prices (`PackProductCatalog.UsagePacks`). The retired
  // minutes SKUs are NOT listed: `/billing/pack/purchase` refuses every axis SKU in BOTH switch
  // positions, so offering one here could only ever produce a failed call. The key keeps its
  // historical `hourPacks` name because the harness endpoint that reads it does.
  hourPacks: [
    { label: '60 points ($7.99)', value: 'ai.qolsync.pack.usage.60' },
    { label: '200 points ($24.99)', value: 'ai.qolsync.pack.usage.200' },
    { label: '600 points ($69.99)', value: 'ai.qolsync.pack.usage.600' },
  ],
  languages: [
    { label: 'English (US)', value: 'en-US' },
    { label: 'Spanish', value: 'es-ES' },
    { label: 'French', value: 'fr-FR' },
    { label: 'German', value: 'de-DE' },
    { label: 'Japanese', value: 'ja-JP' },
    { label: 'Korean', value: 'ko-KR' },
    { label: 'Chinese (Simplified)', value: 'zh-CN' },
    { label: 'Portuguese (BR)', value: 'pt-BR' },
  ],
  refundReasons: [
    { label: 'Duplicate', value: 'duplicate' },
    { label: 'Fraudulent', value: 'fraudulent' },
    { label: 'Requested by customer', value: 'requested_by_customer' },
  ],
  exportFormats: [
    { label: 'JSON', value: 'json' },
    { label: 'CSV', value: 'csv' },
    { label: 'PDF', value: 'pdf' },
  ],
};

// Helper: generates a random session-like ID
export function generateSessionId() {
  const hex = Array.from(crypto.getRandomValues(new Uint8Array(12)))
    .map(b => b.toString(16).padStart(2, '0')).join('');
  return `sess_${hex}`;
}

export function generateUUID() {
  return crypto.randomUUID();
}

// ---------------------------------------------------------------------------
// Endpoint groups
// ---------------------------------------------------------------------------
const API_GROUPS = [
  // =========================================================================
  // 1. Health & Utility
  // =========================================================================
  {
    id: 'health',
    name: 'Health & Utility',
    icon: 'Activity',
    description: 'System health checks and connectivity tests',
    endpoints: [
      {
        id: 'health-check',
        name: 'Health Check',
        method: 'GET',
        path: '/health',
        description: 'Verify the Functions API is running and healthy',
        params: [],
      },
      {
        id: 'protected-check',
        name: 'Protected User Info',
        method: 'GET',
        path: '/protected/me',
        description: 'Returns current authenticated user info (JWT required)',
        auth: 'jwt',
        params: [],
      },
    ],
  },

  // =========================================================================
  // 2. Authentication
  // =========================================================================
  {
    id: 'auth',
    name: 'Authentication',
    icon: 'Lock',
    description: 'User registration, login, and password management',
    endpoints: [
      {
        id: 'auth-register',
        name: 'Register',
        method: 'POST',
        path: '/auth/register',
        description: 'Register a new user account',
        params: [
          { name: 'email', location: 'body', type: 'text', required: true, default: 'test@example.com', placeholder: 'test@example.com' },
          { name: 'password', location: 'body', type: 'text', required: true, default: '', placeholder: 'Min 8 chars, incl. uppercase + number + symbol' },
          { name: 'name', location: 'body', type: 'text', required: false, default: 'Test User', placeholder: 'Display name' },
        ],
      },
      {
        id: 'auth-login-email',
        name: 'Login (Email)',
        method: 'POST',
        path: '/auth/login/email',
        description: 'Authenticate with email and password',
        params: [
          { name: 'email', location: 'body', type: 'text', required: true, default: '', placeholder: 'user@example.com' },
          { name: 'password', location: 'body', type: 'text', required: true, default: '', placeholder: 'Password' },
        ],
      },
      {
        id: 'auth-login-oauth',
        name: 'Login (OAuth)',
        method: 'POST',
        path: '/auth/login/oauth',
        description: 'NOT EXERCISABLE FROM THIS HARNESS, by design (issue #2923). The route requires a signed X-OAuth-Handoff that src/API mints after a real authorization-code exchange, and the handoff is bound to BOTH the identity triple AND a SHA-256 digest of the exact request body — so a pasted token would have to match this form\'s serialized body byte for byte (key order, whitespace, omitted vs empty fields) within 120 seconds. Pasting a handoff answers 401 handoff_required. To exercise the OAuth entrance, drive a real provider sign-in through src/API, or use the integration harness src/Functions/integration-tests/oauth-handoff.sh, which mints over the body it posts.',
        params: [
          { name: 'X-OAuth-Handoff', location: 'header', type: 'text', required: true, default: '', placeholder: 'Signed handoff minted by src/API after the provider exchange' },
          { name: 'provider', location: 'body', type: 'select', required: true, default: 'google', options: [
            { label: 'Google', value: 'google' },
            { label: 'Microsoft', value: 'microsoft' },
            { label: 'Zoom', value: 'zoom' },
            { label: 'Apple', value: 'apple' },
          ]},
          { name: 'providerUserId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Provider user ID' },
          { name: 'email', location: 'body', type: 'text', required: true, default: '', placeholder: 'user@example.com' },
          { name: 'name', location: 'body', type: 'text', required: false, default: '', placeholder: 'Display name' },
        ],
      },
      {
        id: 'auth-forgot-password',
        name: 'Forgot Password',
        method: 'POST',
        path: '/auth/forgot-password',
        description: 'Send password reset email',
        params: [
          { name: 'email', location: 'body', type: 'text', required: true, default: '', placeholder: 'user@example.com' },
        ],
      },
      {
        id: 'auth-admin-reset',
        name: 'Admin Reset Password',
        method: 'POST',
        path: '/auth/admin-reset-password',
        description: 'Admin-initiated password reset (no email verification)',
        dangerLevel: 'warning',
        dangerMessage: 'Resets password without email verification',
        params: [
          { name: 'userId', location: 'body', type: 'text', required: true, default: '', placeholder: 'User ID' },
          { name: 'tenantId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
          { name: 'newPassword', location: 'body', type: 'text', required: true, default: '', placeholder: 'New password' },
        ],
      },
      {
        id: 'auth-get-providers',
        name: 'Get Linked Providers',
        method: 'GET',
        path: '/auth/providers',
        description: 'List linked OAuth providers for a user',
        auth: 'function-key+jwt',
        params: [
          { name: 'userId', location: 'query', type: 'text', required: true, default: '', placeholder: 'User ID' },
        ],
      },
    ],
  },

  // =========================================================================
  // 3. Session Management
  // =========================================================================
  {
    id: 'session',
    name: 'Session',
    icon: 'Radio',
    description: 'Translation session lifecycle management',
    endpoints: [
      {
        id: 'session-create',
        name: 'Create Session',
        method: 'POST',
        path: '/translation/create-session',
        description: 'Create a new real-time translation session',
        params: [
          { name: 'sessionId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Auto-generate or enter session ID', generate: 'sessionId' },
          { name: 'tenantId', location: 'body', type: 'text', required: false, default: '', placeholder: 'Tenant ID (uses current user if empty)' },
          { name: 'targetLanguages', location: 'body', type: 'json', required: false, default: '["es-ES", "fr-FR", "ja-JP"]', placeholder: '["es-ES", "fr-FR"]' },
          { name: 'glossaryId', location: 'body', type: 'text', required: false, default: '', placeholder: 'Glossary ID (optional)' },
        ],
      },
      {
        id: 'session-status',
        name: 'Get Session Status',
        method: 'GET',
        path: '/session/status/{sessionId}',
        description: 'Get current status of a translation session',
        params: [
          { name: 'sessionId', location: 'route', type: 'text', required: true, default: '', placeholder: 'Session ID' },
        ],
      },
      {
        id: 'session-status-v2',
        name: 'Get Session Status (v2)',
        method: 'GET',
        path: '/translation/get-session-status',
        description: 'Get session status with optional password check',
        params: [
          { name: 'sessionId', location: 'query', type: 'text', required: true, default: '', placeholder: 'Session ID' },
          { name: 'password', location: 'query', type: 'text', required: false, default: '', placeholder: 'Session password (optional)' },
        ],
      },
      {
        id: 'session-set-language',
        name: 'Set Speaker Language',
        method: 'POST',
        path: '/translation/set-speaker-language',
        description: 'Set the source language for the speaker',
        params: [
          { name: 'sessionId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Session ID' },
          { name: 'sourceLanguage', location: 'body', type: 'select', required: true, default: 'en-US', options: TEST_DATA.languages },
          { name: 'tenantId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
        ],
      },
      {
        id: 'session-pause',
        name: 'Pause Session',
        method: 'POST',
        path: '/translation/pause-session',
        description: 'Pause an active translation session',
        dangerLevel: 'warning',
        dangerMessage: 'Pauses live translation for all participants',
        params: [
          { name: 'sessionId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Session ID' },
          { name: 'tenantId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
        ],
      },
      {
        id: 'session-resume',
        name: 'Resume Session',
        method: 'POST',
        path: '/translation/resume-session',
        description: 'Resume a paused translation session',
        params: [
          { name: 'sessionId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Session ID' },
          { name: 'tenantId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
        ],
      },
      {
        id: 'session-stop',
        name: 'Stop Session',
        method: 'POST',
        path: '/translation/stop-session',
        description: 'Stop and end a translation session',
        dangerLevel: 'critical',
        dangerMessage: 'Permanently ends the session — cannot be undone',
        params: [
          { name: 'sessionId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Session ID' },
          { name: 'tenantId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
        ],
      },
      {
        id: 'session-plan',
        name: 'Get Session Plan',
        method: 'GET',
        path: '/session/{sessionId}/plan',
        description: 'Get billing plan and feature limits for a session',
        params: [
          { name: 'sessionId', location: 'route', type: 'text', required: true, default: '', placeholder: 'Session ID' },
        ],
      },
    ],
  },

  // =========================================================================
  // 4. Q&A
  // =========================================================================
  {
    id: 'qa',
    name: 'Q&A',
    icon: 'MessageCircle',
    description: 'Session Q&A: questions, replies, voting, moderation',
    endpoints: [
      {
        id: 'qa-get-questions',
        name: 'Get Questions',
        method: 'GET',
        path: '/qa/questions',
        description: 'List all questions for a session',
        params: [
          { name: 'sessionId', location: 'query', type: 'text', required: true, default: '', placeholder: 'Session ID' },
        ],
      },
      {
        id: 'qa-post-question',
        name: 'Post Question',
        method: 'POST',
        path: '/qa/questions',
        description: 'Submit a new question to a session',
        params: [
          { name: 'sessionId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Session ID' },
          { name: 'text', location: 'body', type: 'textarea', required: true, default: 'What is the main topic of today\'s meeting?', placeholder: 'Question text' },
          { name: 'authorName', location: 'body', type: 'text', required: false, default: 'Test User', placeholder: 'Author name' },
          { name: 'isAnonymous', location: 'body', type: 'boolean', required: true, default: false },
          { name: 'originalLanguage', location: 'body', type: 'select', required: false, default: 'en-US', options: TEST_DATA.languages },
        ],
      },
      {
        id: 'qa-delete-question',
        name: 'Delete Question',
        method: 'DELETE',
        path: '/qa/questions/{questionId}',
        description: 'Delete a question (moderators only)',
        dangerLevel: 'critical',
        dangerMessage: 'Permanently deletes the question and all its replies',
        params: [
          { name: 'questionId', location: 'route', type: 'text', required: true, default: '', placeholder: 'Question ID' },
          { name: 'sessionId', location: 'query', type: 'text', required: true, default: '', placeholder: 'Session ID' },
        ],
      },
      {
        id: 'qa-answer-question',
        name: 'Answer Question',
        method: 'POST',
        path: '/qa/questions/{questionId}/answer',
        description: 'Post an answer to a question',
        params: [
          { name: 'questionId', location: 'route', type: 'text', required: true, default: '', placeholder: 'Question ID' },
          { name: 'sessionId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Session ID' },
          { name: 'answer', location: 'body', type: 'textarea', required: true, default: '', placeholder: 'Answer text' },
          { name: 'isPrivateAnswer', location: 'body', type: 'boolean', required: false, default: false },
        ],
      },
      {
        id: 'qa-upvote',
        name: 'Upvote Question',
        method: 'POST',
        path: '/qa/questions/{questionId}/upvote',
        description: 'Upvote a question',
        params: [
          { name: 'questionId', location: 'route', type: 'text', required: true, default: '', placeholder: 'Question ID' },
          { name: 'sessionId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Session ID' },
          { name: 'X-Participant-Id', location: 'header', type: 'text', required: false, default: '', placeholder: 'Participant ID' },
        ],
      },
      {
        id: 'qa-get-tags',
        name: 'Get Tags',
        method: 'GET',
        path: '/qa/tags',
        description: 'List all tags for a session',
        params: [
          { name: 'sessionId', location: 'query', type: 'text', required: true, default: '', placeholder: 'Session ID' },
        ],
      },
      {
        id: 'qa-create-tag',
        name: 'Create Tag',
        method: 'POST',
        path: '/qa/tags',
        description: 'Create a new tag for categorizing questions',
        params: [
          { name: 'sessionId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Session ID' },
          { name: 'name', location: 'body', type: 'text', required: true, default: 'Important', placeholder: 'Tag name' },
        ],
      },
      {
        id: 'qa-export',
        name: 'Export Questions',
        method: 'GET',
        path: '/qa/questions/export',
        description: 'Export all questions for a session',
        params: [
          { name: 'sessionId', location: 'query', type: 'text', required: true, default: '', placeholder: 'Session ID' },
          { name: 'format', location: 'query', type: 'select', required: false, default: 'json', options: TEST_DATA.exportFormats },
        ],
      },
    ],
  },

  // =========================================================================
  // 5. Billing
  // =========================================================================
  {
    id: 'billing',
    name: 'Billing',
    icon: 'CreditCard',
    description: 'Session billing, points packs, event passes, and subscriptions',
    endpoints: [
      {
        id: 'billing-purchase-hourpack',
        name: 'Purchase Usage Pack',
        method: 'POST',
        path: '/billing/pack/purchase',
        description: 'Purchase a usage-credit pack (same SKUs and prices as iOS); tenant comes from the JWT, no subscription required. Refused unless usage-pack sales are switched on.',
        auth: 'jwt',
        dangerLevel: 'warning',
        dangerMessage: 'Initiates a Stripe payment',
        params: [
          { name: 'productId', location: 'body', type: 'select', required: true, default: 'ai.qolsync.pack.usage.200', options: TEST_DATA.hourPacks },
          { name: 'successUrl', location: 'body', type: 'text', required: false, default: '', placeholder: 'Redirect URL on success' },
          { name: 'cancelUrl', location: 'body', type: 'text', required: false, default: '', placeholder: 'Redirect URL on cancel' },
        ],
      },
      {
        id: 'billing-purchase-eventpass',
        name: 'Purchase Event Pass',
        method: 'POST',
        path: '/billing/event-pass/purchase',
        description: 'Purchase a flat-rate event pass for webinars',
        auth: 'function-key+jwt',
        dangerLevel: 'warning',
        dangerMessage: 'Initiates a Stripe payment',
        params: [
          { name: 'tenantId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
          { name: 'eventPassId', location: 'body', type: 'select', required: true, default: 'event_pass_starter', options: TEST_DATA.eventPassTiers },
          { name: 'successUrl', location: 'body', type: 'text', required: false, default: '', placeholder: 'Redirect URL on success' },
          { name: 'cancelUrl', location: 'body', type: 'text', required: false, default: '', placeholder: 'Redirect URL on cancel' },
        ],
      },
      {
        id: 'billing-get-subscription',
        name: 'Get Subscription',
        method: 'GET',
        path: '/platform/tenant/{tenantId}/subscription',
        description: 'Inspect a tenant subscription with platform read permission',
        auth: 'jwt',
        params: [
          { name: 'line', location: 'query', type: 'select', required: false, default: 'meeting', options: [{ label: 'Meeting', value: 'meeting' }, { label: 'Webinar', value: 'webinar' }] },
          { name: 'tenantId', location: 'route', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
        ],
      },
      {
        id: 'billing-cancel-subscription',
        name: 'Cancel Subscription',
        method: 'POST',
        path: '/billing/subscription/{subscriptionId}/cancel',
        description: 'Cancel a Stripe subscription at period end',
        auth: 'function-key+jwt',
        dangerLevel: 'critical',
        dangerMessage: 'Permanently cancels the subscription — cannot be undone',
        params: [
          { name: 'subscriptionId', location: 'route', type: 'text', required: true, default: '', placeholder: 'Stripe subscription ID (sub_xxx)' },
        ],
      },
      {
        id: 'billing-admin-refund',
        name: 'Admin Refund',
        method: 'POST',
        path: '/billing/admin/refund',
        description: 'Issue a refund via Stripe (admin only)',
        auth: 'function-key+jwt',
        dangerLevel: 'critical',
        dangerMessage: 'Issues a real Stripe refund — money is returned to the customer',
        params: [
          { name: 'paymentIntentId', location: 'body', type: 'text', required: true, default: '', placeholder: 'pi_xxx (Stripe PaymentIntent ID)' },
          { name: 'amountCents', location: 'body', type: 'number', required: false, default: '', placeholder: 'Amount in cents (blank = full refund)' },
          { name: 'reason', location: 'body', type: 'select', required: false, default: 'requested_by_customer', options: TEST_DATA.refundReasons },
          { name: 'internalNotes', location: 'body', type: 'text', required: false, default: '', placeholder: 'Internal notes (not sent to customer)' },
        ],
      },
    ],
  },

  // =========================================================================
  // 6. Glossary
  // =========================================================================
  {
    id: 'glossary',
    name: 'Glossary',
    icon: 'BookOpen',
    description: 'Glossary drafts, generation, and document management',
    endpoints: [
      {
        id: 'glossary-get-draft',
        name: 'Get Draft',
        method: 'GET',
        path: '/glossary/draft',
        description: 'Get the current glossary draft for a tenant',
        params: [
          { name: 'tenantId', location: 'query', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
        ],
      },
      {
        id: 'glossary-save-draft',
        name: 'Save Draft',
        method: 'PUT',
        path: '/glossary/draft',
        description: 'Save or update a glossary draft',
        params: [
          { name: 'tenantId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
          { name: 'glossary', location: 'body', type: 'json', required: false, default: '{"terms": [{"source": "MeetingSync", "target": "MeetingSync", "language": "en-US"}]}', placeholder: 'Glossary JSON' },
        ],
      },
      {
        id: 'glossary-delete-draft',
        name: 'Delete Draft',
        method: 'DELETE',
        path: '/glossary/draft',
        description: 'Delete a glossary draft',
        dangerLevel: 'warning',
        dangerMessage: 'Deletes the draft — this cannot be undone',
        params: [
          { name: 'tenantId', location: 'query', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
        ],
      },
      {
        id: 'glossary-debug-translate',
        name: 'Debug Translate',
        method: 'POST',
        path: '/glossary/debug-translate',
        description: 'Translate text with glossary debugging info',
        auth: 'function-key',
        params: [
          { name: 'text', location: 'body', type: 'textarea', required: true, default: 'Welcome to today\'s meeting about MeetingSync features.', placeholder: 'Text to translate' },
          { name: 'sourceLanguage', location: 'body', type: 'select', required: true, default: 'en-US', options: TEST_DATA.languages },
          { name: 'targetLanguage', location: 'body', type: 'select', required: true, default: 'es-ES', options: TEST_DATA.languages },
        ],
      },
      {
        id: 'glossary-generate',
        name: 'Generate Glossary',
        method: 'POST',
        path: '/glossary/generate',
        description: 'Generate a glossary from uploaded documents using AI',
        auth: 'function-key',
        dangerLevel: 'warning',
        dangerMessage: 'Triggers AI generation — consumes OpenAI credits',
        params: [
          { name: 'tenantId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
          { name: 'documentIds', location: 'body', type: 'json', required: true, default: '[]', placeholder: '["doc-id-1", "doc-id-2"]' },
          { name: 'sourceLanguage', location: 'body', type: 'select', required: false, default: 'en-US', options: TEST_DATA.languages },
          { name: 'targetLanguage', location: 'body', type: 'select', required: false, default: 'es-ES', options: TEST_DATA.languages },
        ],
      },
      {
        id: 'glossary-ingestion-status',
        name: 'Get Ingestion Status',
        method: 'GET',
        path: '/glossary/ingestion-status',
        description: 'Check glossary document ingestion progress',
        params: [
          { name: 'tenantId', location: 'query', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
        ],
      },
    ],
  },

  // =========================================================================
  // 7. Meeting AI
  // =========================================================================
  {
    id: 'meeting-ai',
    name: 'Meeting AI',
    icon: 'Brain',
    description: 'Meeting summaries, transcripts, AI questions, and templates',
    endpoints: [
      {
        id: 'meetingai-list-meetings',
        name: 'List Meetings',
        method: 'GET',
        path: '/meeting-ai/meetings',
        description: 'List all meetings for a tenant',
        params: [
          { name: 'tenantId', location: 'query', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
        ],
      },
      {
        id: 'meetingai-summary',
        name: 'Get Meeting Summary',
        method: 'POST',
        path: '/meeting-ai/summary',
        description: 'Generate or retrieve AI meeting summary',
        dangerLevel: 'warning',
        dangerMessage: 'May trigger AI generation — consumes OpenAI credits',
        params: [
          { name: 'sessionId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Session ID' },
          { name: 'meetingTranscriptId', location: 'body', type: 'text', required: false, default: '', placeholder: 'Transcript ID (optional)' },
          { name: 'password', location: 'body', type: 'text', required: false, default: '', placeholder: 'Session password (optional)' },
        ],
      },
      {
        id: 'meetingai-list-archives',
        name: 'List Archives',
        method: 'GET',
        path: '/meeting-ai/archives',
        description: 'List meeting transcript archives',
        params: [
          { name: 'sessionId', location: 'query', type: 'text', required: true, default: '', placeholder: 'Session ID' },
        ],
      },
      {
        id: 'meetingai-download-transcript',
        name: 'Download Transcript',
        method: 'GET',
        path: '/meeting-ai/transcript/{meetingTranscriptId}',
        description: 'Download a meeting transcript',
        params: [
          { name: 'meetingTranscriptId', location: 'route', type: 'text', required: true, default: '', placeholder: 'Transcript ID' },
          { name: 'sessionId', location: 'query', type: 'text', required: false, default: '', placeholder: 'Session ID' },
          { name: 'format', location: 'query', type: 'select', required: false, default: 'json', options: TEST_DATA.exportFormats },
        ],
      },
      {
        id: 'meetingai-ask-question',
        name: 'Ask Meeting Question',
        method: 'POST',
        path: '/meeting-ai/ask',
        description: 'Ask an AI question about a meeting transcript',
        dangerLevel: 'warning',
        dangerMessage: 'Triggers AI inference — consumes OpenAI credits',
        params: [
          { name: 'sessionId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Session ID' },
          { name: 'question', location: 'body', type: 'textarea', required: true, default: 'What were the key action items from this meeting?', placeholder: 'Your question about the meeting' },
          { name: 'language', location: 'body', type: 'select', required: false, default: 'en-US', options: TEST_DATA.languages },
        ],
      },
      {
        id: 'meetingai-list-templates',
        name: 'List Templates',
        method: 'GET',
        path: '/meeting-ai/templates',
        description: 'Get question templates for a tenant',
        params: [
          { name: 'tenantId', location: 'query', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
        ],
      },
      {
        id: 'meetingai-list-all-templates',
        name: 'List All Templates (Admin)',
        method: 'GET',
        path: '/meeting-ai/templates/all',
        description: 'List all question templates across tenants',
        auth: 'function-key',
        params: [],
      },
    ],
  },

  // =========================================================================
  // 8. Admin / SuperAdmin
  // =========================================================================
  {
    id: 'admin',
    name: 'Admin',
    icon: 'Shield',
    description: 'Tenant and user management (SuperAdmin), the platform usage-catalog rate table, and one tenant\'s usage-credit ledger + operator adjustments',
    endpoints: [
      {
        id: 'admin-list-tenants',
        name: 'List Tenants',
        method: 'GET',
        path: '/superadmin/tenants',
        description: 'List all tenants (paginated)',
        auth: 'jwt',
        params: [
          { name: 'skip', location: 'query', type: 'number', required: false, default: 0, placeholder: '0' },
          { name: 'take', location: 'query', type: 'number', required: false, default: 20, placeholder: '20' },
        ],
      },
      {
        id: 'admin-get-tenant',
        name: 'Get Tenant',
        method: 'GET',
        path: '/superadmin/tenants/{tenantId}',
        description: 'Get detailed tenant information',
        auth: 'jwt',
        params: [
          { name: 'tenantId', location: 'route', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
        ],
      },
      {
        id: 'admin-create-tenant',
        name: 'Create Tenant',
        method: 'POST',
        path: '/superadmin/tenants',
        description: 'Create a new tenant',
        auth: 'jwt',
        dangerLevel: 'warning',
        dangerMessage: 'Creates a new tenant in the system',
        params: [
          { name: 'name', location: 'body', type: 'text', required: true, default: '', placeholder: 'Tenant name' },
          { name: 'organizationName', location: 'body', type: 'text', required: false, default: '', placeholder: 'Organization name' },
          { name: 'email', location: 'body', type: 'text', required: false, default: '', placeholder: 'Contact email' },
        ],
      },
      {
        id: 'admin-list-users',
        name: 'List Users',
        method: 'GET',
        path: '/superadmin/tenants/{tenantId}/users',
        description: 'List all users in a tenant',
        auth: 'jwt',
        params: [
          { name: 'tenantId', location: 'route', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
        ],
      },
      {
        id: 'admin-create-user',
        name: 'Create User',
        method: 'POST',
        path: '/superadmin/tenants/{tenantId}/users',
        description: 'Create a new user in a tenant',
        auth: 'jwt',
        dangerLevel: 'warning',
        dangerMessage: 'Creates a new user account',
        params: [
          { name: 'tenantId', location: 'route', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
          { name: 'email', location: 'body', type: 'text', required: true, default: '', placeholder: 'user@example.com' },
          { name: 'name', location: 'body', type: 'text', required: false, default: '', placeholder: 'Display name' },
          { name: 'role', location: 'body', type: 'select', required: false, default: 'Member', options: [
            { label: 'Owner', value: 'Owner' },
            { label: 'Admin', value: 'Admin' },
            { label: 'Member', value: 'Member' },
            { label: 'Viewer', value: 'Viewer' },
          ]},
        ],
      },
      {
        id: 'admin-delete-tenant',
        name: 'Delete Tenant',
        method: 'DELETE',
        path: '/superadmin/tenants/{tenantId}',
        description: 'Delete a tenant and all associated data',
        auth: 'jwt',
        dangerLevel: 'critical',
        dangerMessage: 'PERMANENTLY deletes the tenant, all users, sessions, and billing data',
        params: [
          { name: 'tenantId', location: 'route', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
        ],
      },
      {
        id: 'admin-delete-user',
        name: 'Delete User',
        method: 'DELETE',
        path: '/superadmin/tenants/{tenantId}/users/{userId}',
        description: 'Delete a user account from a tenant',
        auth: 'jwt',
        dangerLevel: 'critical',
        dangerMessage: 'PERMANENTLY deletes the user account',
        params: [
          { name: 'tenantId', location: 'route', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
          { name: 'userId', location: 'route', type: 'text', required: true, default: '', placeholder: 'User ID' },
        ],
      },
      {
        id: 'admin-cleanup-stale',
        name: 'Cleanup Stale Connections',
        method: 'POST',
        path: '/admin/cleanup-stale-connections',
        description: 'Remove stale SignalR connections',
        auth: 'function-key',
        dangerLevel: 'warning',
        dangerMessage: 'Removes stale connections — may disconnect idle users',
        params: [
          { name: 'ageDays', location: 'query', type: 'number', required: false, default: 7, placeholder: 'Age in days (default: 7)' },
        ],
      },

      // ---------------------------------------------------------------------
      // Usage catalog & rates (platform pricing, role >= 3 read / >= 4 write).
      // Grouped here rather than in a group of their own: these are the
      // platform-admin write surface, the same audience as the rest of this
      // group, and every consumer of API_GROUPS (and its tests) pins the group
      // count. Rates are MILLI on the wire — 1 point = 1000 milli, and
      // meeting.extraParticipantMinute is 300 milli = 0.3 points, so a rate is
      // not necessarily a whole number of points.
      // ---------------------------------------------------------------------
      {
        id: 'usage-catalog-get',
        name: 'Get Usage Catalog',
        method: 'GET',
        path: '/platform/usage-catalog',
        description: 'Read the stored catalog, or the compiled default when none has ever been saved. Returns { document, source, etag, enginePropagationSeconds } — the TOP-LEVEL etag is what a PUT\'s If-Match must carry, not a field inside document. Role >= 3 (PlatformReadOnly).',
        auth: 'jwt',
        params: [],
      },
      {
        id: 'usage-catalog-validate',
        name: 'Validate Usage Catalog',
        method: 'POST',
        path: '/platform/usage-catalog/validate',
        description: 'Grade a candidate catalog WITHOUT storing it. Returns { valid, violations, warnings }. An empty reason is itself a violation (REASON_REQUIRED), so validate with the reason you intend to save. Role >= 4 (PlatformContributor).',
        auth: 'jwt',
        params: [
          { name: 'reason', location: 'body', type: 'text', required: true, default: '', placeholder: 'Why this change is being made (5-500 chars)' },
          { name: 'catalogVersion', location: 'body', type: 'text', required: true, default: 'usage-catalog-v4', placeholder: 'usage-catalog-v4' },
          { name: 'rateVersion', location: 'body', type: 'text', required: true, default: 'usage-rates-v6', placeholder: 'usage-rates-v6' },
          { name: 'currency', location: 'body', type: 'text', required: false, default: 'USD', placeholder: 'Omit to carry the stored currency forward' },
          { name: 'effectiveAtUtc', location: 'body', type: 'text', required: true, default: '', placeholder: '2026-10-01T00:00:00Z' },
          { name: 'ratesMilli', location: 'body', type: 'json', required: true, default: '{"realtime.conversation": 1000, "realtime.transcription": 500, "realtime.simultaneous": 1000, "realtime.normal": 1000, "realtime.normal.partials": 1000, "realtime.advanced": 1000, "realtime.advanced.partials": 1000, "content.audioMinute": 500, "content.translationMinuteLanguage": 500, "content.documentUnitLanguage": 1000, "capture.translationUnitLanguage": 2000, "tts.outputMinute": 1000, "meetingAi.unit.s": 500, "meetingAi.unit.m": 1000, "meetingAi.unit.l": 3000, "meetingAi.unit.xl": 6000, "capture.comprehendUnit": 1000, "meeting.extraParticipantMinute": 60}', placeholder: 'All 18 rate keys, in MILLI (1 point = 1000 milli)' },
          { name: 'monthlyGrantMilli', location: 'body', type: 'json', required: true, default: '{"plan_meeting_v2": 200000, "plan_meeting_pro_v2": 250000, "plan_webinar_v2": 300000, "plan_webinar_100_v2": 300000, "plan_webinar_300_v2": 300000, "plan_meetings_starter_v1": 600000, "plan_meetings_business_v1": 900000}', placeholder: 'Monthly grant per plan id, in milli' },
          { name: 'trialGrantMilli', location: 'body', type: 'number', required: true, default: 30000, placeholder: '30000 milli = 30 points' },
          { name: 'extraLanguagePercent', location: 'body', type: 'json', required: false, default: '{"plan_meeting_v2": 0, "plan_meeting_pro_v2": 0, "plan_meetings_starter_v1": 0, "plan_meetings_business_v1": 0}', placeholder: 'Plan id -> % of one language each extra language adds (0-100). Omit = pre-v6 shape: 100% for every plan' },
          { name: 'monthlyIncludedUnits', location: 'body', type: 'json', required: false, default: '{"plan_meeting_v2": {"meetingAi.unit.s": 20, "meetingAi.unit.m": 5, "capture.translationUnitLanguage": 4, "content.documentUnitLanguage": 15}, "plan_meeting_pro_v2": {"meetingAi.unit.s": 30, "meetingAi.unit.m": 5, "capture.translationUnitLanguage": 4, "capture.comprehendUnit": 5, "content.documentUnitLanguage": 20}}', placeholder: 'Plan id -> rate key -> units included monthly. Omit = nothing included; meetingAi.unit.xl is refused' },
        ],
      },
      {
        id: 'usage-catalog-put',
        name: 'Save Usage Catalog Draft',
        method: 'PUT',
        path: '/platform/usage-catalog',
        description: 'Save the candidate as a DRAFT — no consumer reads a draft, so publish is a separate step. If-Match is required on EVERY write (every write is a price change): the ETag from GET, or * when nothing is stored yet. 428 USAGE_CATALOG_VERSION_REQUIRED, 412 USAGE_CATALOG_VERSION_CONFLICT, 400 REASON_REQUIRED or USAGE_CATALOG_INVALID (+ violations). Role >= 4.',
        auth: 'jwt',
        dangerLevel: 'warning',
        dangerMessage: 'Replaces the stored catalog document as a draft in the selected environment',
        params: [
          { name: 'If-Match', location: 'header', type: 'text', required: true, default: '*', placeholder: 'ETag from GET, or * to create the first catalog' },
          { name: 'reason', location: 'body', type: 'text', required: true, default: '', placeholder: 'Why this change is being made (5-500 chars)' },
          { name: 'catalogVersion', location: 'body', type: 'text', required: true, default: 'usage-catalog-v4', placeholder: 'usage-catalog-v4' },
          { name: 'rateVersion', location: 'body', type: 'text', required: true, default: 'usage-rates-v6', placeholder: 'usage-rates-v6' },
          { name: 'currency', location: 'body', type: 'text', required: false, default: 'USD', placeholder: 'Omit to carry the stored currency forward' },
          { name: 'effectiveAtUtc', location: 'body', type: 'text', required: true, default: '', placeholder: '2026-10-01T00:00:00Z' },
          { name: 'ratesMilli', location: 'body', type: 'json', required: true, default: '{"realtime.conversation": 1000, "realtime.transcription": 500, "realtime.simultaneous": 1000, "realtime.normal": 1000, "realtime.normal.partials": 1000, "realtime.advanced": 1000, "realtime.advanced.partials": 1000, "content.audioMinute": 500, "content.translationMinuteLanguage": 500, "content.documentUnitLanguage": 1000, "capture.translationUnitLanguage": 2000, "tts.outputMinute": 1000, "meetingAi.unit.s": 500, "meetingAi.unit.m": 1000, "meetingAi.unit.l": 3000, "meetingAi.unit.xl": 6000, "capture.comprehendUnit": 1000, "meeting.extraParticipantMinute": 60}', placeholder: 'All 18 rate keys, in MILLI (1 point = 1000 milli)' },
          { name: 'monthlyGrantMilli', location: 'body', type: 'json', required: true, default: '{"plan_meeting_v2": 200000, "plan_meeting_pro_v2": 250000, "plan_webinar_v2": 300000, "plan_webinar_100_v2": 300000, "plan_webinar_300_v2": 300000, "plan_meetings_starter_v1": 600000, "plan_meetings_business_v1": 900000}', placeholder: 'Monthly grant per plan id, in milli' },
          { name: 'trialGrantMilli', location: 'body', type: 'number', required: true, default: 30000, placeholder: '30000 milli = 30 points' },
          { name: 'extraLanguagePercent', location: 'body', type: 'json', required: false, default: '{"plan_meeting_v2": 0, "plan_meeting_pro_v2": 0, "plan_meetings_starter_v1": 0, "plan_meetings_business_v1": 0}', placeholder: 'Plan id -> % of one language each extra language adds (0-100). Omit = pre-v6 shape: 100% for every plan' },
          { name: 'monthlyIncludedUnits', location: 'body', type: 'json', required: false, default: '{"plan_meeting_v2": {"meetingAi.unit.s": 20, "meetingAi.unit.m": 5, "capture.translationUnitLanguage": 4, "content.documentUnitLanguage": 15}, "plan_meeting_pro_v2": {"meetingAi.unit.s": 30, "meetingAi.unit.m": 5, "capture.translationUnitLanguage": 4, "capture.comprehendUnit": 5, "content.documentUnitLanguage": 20}}', placeholder: 'Plan id -> rate key -> units included monthly. Omit = nothing included; meetingAi.unit.xl is refused' },
        ],
      },
      {
        id: 'usage-catalog-publish',
        name: 'Publish Usage Catalog',
        method: 'POST',
        path: '/platform/usage-catalog/publish',
        description: 'Flip the saved draft to published. No body, no If-Match. Returns { catalogVersion, rateVersion, effectiveAtUtc, version } only — NOT the document, so re-read with GET afterwards. 409 USAGE_CATALOG_NOT_VALIDATED when the saved draft no longer validates. Role >= 4.',
        auth: 'jwt',
        dangerLevel: 'critical',
        dangerMessage: 'Prices every new session start in the selected environment from the saved draft',
        params: [],
      },
      {
        id: 'usage-ledger-get',
        name: 'Get Tenant Usage Ledger',
        method: 'GET',
        path: '/platform/tenants/{tenantId}/usage-ledger',
        description: "One tenant's usage-credit ledger: { balance, seats, grants, malformedGrants, operations: { items, nextCursor }, stuckReservations }. `balance.balanceVersion` is the optimistic-lock key every adjustment must echo back as If-Match. A tenant with no usage wallet answers 404 TENANT_NOT_FOUND — it never answers a zero balance. Role >= 3 (PlatformReadOnly).",
        auth: 'jwt',
        params: [
          { name: 'tenantId', location: 'route', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
          { name: 'cursor', location: 'query', type: 'text', required: false, default: '', placeholder: 'nextCursor from the previous page' },
          { name: 'limit', location: 'query', type: 'number', required: false, default: 50, placeholder: '50' },
        ],
      },
      {
        id: 'usage-adjustment-preview',
        name: 'Preview Usage Adjustment',
        method: 'POST',
        path: '/platform/tenants/{tenantId}/usage-adjustments/preview',
        description: "Project what an adjustment would do — { before, after, affectedGrants, warnings } — WITHOUT writing anything. Same payload and same refusals as the write below, so use it to check a payload before applying it. Role >= 4 (PlatformContributor).",
        auth: 'jwt',
        params: [
          { name: 'tenantId', location: 'route', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
          { name: 'If-Match', location: 'header', type: 'text', required: true, default: '', placeholder: 'balance.balanceVersion from the GET above' },
          { name: 'kind', location: 'body', type: 'select', required: true, default: 'compensate', options: [{ label: 'compensate (add points)', value: 'compensate' }, { label: 'clawback (take points back)', value: 'clawback' }] },
          { name: 'amountMilli', location: 'body', type: 'number', required: true, default: 10000, placeholder: '10000 milli = 10 points. Always POSITIVE — kind carries the sign' },
          { name: 'grantId', location: 'body', type: 'text', required: false, default: '', placeholder: 'Clawback only: the stored grant id (usage-grant:...) to take back from' },
          { name: 'expiryPolicy', location: 'body', type: 'select', required: false, default: 'none', options: [{ label: 'none (permanent)', value: 'none' }, { label: 'custom (expiresAtUtc required)', value: 'custom' }] },
          { name: 'expiresAtUtc', location: 'body', type: 'text', required: false, default: '', placeholder: "custom policy only: a future instant, e.g. 2026-12-31T23:59:59Z" },
          { name: 'reason', location: 'body', type: 'text', required: true, default: '', placeholder: 'Shown to the customer word for word on their Activity page (5-500 chars)' },
          { name: 'ticketId', location: 'body', type: 'text', required: true, default: '', placeholder: 'SUP-4821 — letters, digits, . _ : - only (max 64)' },
        ],
      },
      {
        id: 'usage-adjustment-apply',
        name: 'Apply Usage Adjustment',
        method: 'POST',
        path: '/platform/tenants/{tenantId}/usage-adjustments',
        description: "Apply the adjustment through the ledger's own idempotent batch rails. `ticketId` (body) — NOT the Idempotency-Key header — is the ledger identity: resending the same ticket with the same figures answers 200 alreadyProcessed:true with an UNCHANGED balance, and the same ticket with DIFFERENT figures answers 409. NEVER retry with a fresh ticket: a new ticket is a new identity and writes a SECOND real credit. The refusal code is keyed by the version carrier — no version 428, If-Match mismatch 412, body balanceVersion mismatch 409. 400: AMOUNT_OUT_OF_RANGE / REASON_REQUIRED / TICKET_REQUIRED / GRANT_NOT_FOUND / CLAWBACK_EXCEEDS_REMAINING / SEAT_SCOPED_ADJUSTMENT_UNSUPPORTED / QUOTA_PERIOD_POLICY_UNAVAILABLE / BILLING_EPOCH_UNKNOWN. Role >= 4.",
        auth: 'jwt',
        dangerLevel: 'critical',
        dangerMessage: 'Creates or removes REAL spendable points for this tenant in the selected environment',
        params: [
          { name: 'tenantId', location: 'route', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
          { name: 'If-Match', location: 'header', type: 'text', required: true, default: '', placeholder: 'balance.balanceVersion from the GET above' },
          { name: 'Idempotency-Key', location: 'header', type: 'text', required: false, default: '', placeholder: 'Recorded, never authoritative — ticketId is the real key' },
          { name: 'kind', location: 'body', type: 'select', required: true, default: 'compensate', options: [{ label: 'compensate (add points)', value: 'compensate' }, { label: 'clawback (take points back)', value: 'clawback' }] },
          { name: 'amountMilli', location: 'body', type: 'number', required: true, default: 10000, placeholder: '10000 milli = 10 points. Always POSITIVE — kind carries the sign' },
          { name: 'grantId', location: 'body', type: 'text', required: false, default: '', placeholder: 'Clawback only: the stored grant id (usage-grant:...) to take back from' },
          { name: 'expiryPolicy', location: 'body', type: 'select', required: false, default: 'none', options: [{ label: 'none (permanent)', value: 'none' }, { label: 'custom (expiresAtUtc required)', value: 'custom' }] },
          { name: 'expiresAtUtc', location: 'body', type: 'text', required: false, default: '', placeholder: "custom policy only: a future instant, e.g. 2026-12-31T23:59:59Z" },
          { name: 'reason', location: 'body', type: 'text', required: true, default: '', placeholder: 'Shown to the customer word for word on their Activity page (5-500 chars)' },
          { name: 'ticketId', location: 'body', type: 'text', required: true, default: '', placeholder: 'SUP-4821 — letters, digits, . _ : - only (max 64)' },
          { name: 'confirm', location: 'body', type: 'boolean', required: false, default: true, placeholder: 'false is refused with CONFIRMATION_REQUIRED; omitted proceeds' },
        ],
      },
    ],
  },

  // =========================================================================
  // 9. SignalR
  // =========================================================================
  {
    id: 'signalr',
    name: 'SignalR',
    icon: 'Wifi',
    description: 'Real-time WebSocket connection management',
    endpoints: [
      {
        id: 'signalr-negotiate',
        name: 'Negotiate',
        method: 'POST',
        path: '/negotiate',
        description: 'Negotiate a SignalR connection (returns connection info)',
        params: [
          { name: 'sessionId', location: 'query', type: 'text', required: false, default: '', placeholder: 'Session ID (optional)' },
        ],
      },
      {
        id: 'signalr-subscribe',
        name: 'Subscribe',
        method: 'POST',
        path: '/subscribe',
        description: 'Subscribe to a session\'s translation stream',
        params: [
          { name: 'sessionId', location: 'body', type: 'text', required: true, default: '', placeholder: 'Session ID' },
          { name: 'connectionId', location: 'body', type: 'text', required: true, default: '', placeholder: 'SignalR connection ID (from negotiate)' },
          { name: 'language', location: 'body', type: 'select', required: true, default: 'en-US', options: TEST_DATA.languages },
          { name: 'role', location: 'body', type: 'select', required: true, default: 'participant', options: [
            { label: 'Participant', value: 'participant' },
            { label: 'Host', value: 'host' },
          ]},
        ],
      },
    ],
  },

  // =========================================================================
  // 10. Tenant & Customer
  // =========================================================================
  {
    id: 'tenant',
    name: 'Tenant & Customer',
    icon: 'Building',
    description: 'Tenant management and customer plan queries',
    endpoints: [
      {
        id: 'tenant-list',
        name: 'List Tenants',
        method: 'GET',
        path: '/tenants',
        description: 'List all tenants (public)',
        params: [
          { name: 'skip', location: 'query', type: 'number', required: false, default: 0, placeholder: '0' },
          { name: 'take', location: 'query', type: 'number', required: false, default: 20, placeholder: '20' },
        ],
      },
      {
        id: 'tenant-create',
        name: 'Create Tenant',
        method: 'POST',
        path: '/tenants',
        description: 'Create a new tenant (public registration)',
        params: [
          { name: 'name', location: 'body', type: 'text', required: true, default: '', placeholder: 'Tenant name' },
          { name: 'organizationName', location: 'body', type: 'text', required: false, default: '', placeholder: 'Organization' },
          { name: 'email', location: 'body', type: 'text', required: false, default: '', placeholder: 'Contact email' },
        ],
      },
      {
        id: 'tenant-get-plan',
        name: 'Get User Plan',
        method: 'GET',
        path: '/customer/plan',
        description: 'Get the billing plan for a tenant',
        auth: 'function-key',
        params: [
          { name: 'tenantId', location: 'query', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
        ],
      },
      {
        id: 'tenant-dynamic-rate',
        name: 'Get Dynamic Rate',
        method: 'GET',
        path: '/customer/dynamic-rate',
        description: 'Get the dynamic billing rate for a tenant',
        auth: 'function-key',
        params: [
          { name: 'tenantId', location: 'query', type: 'text', required: true, default: '', placeholder: 'Tenant ID' },
        ],
      },
    ],
  },
];

export default API_GROUPS;
